import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip, Tabs, Tab,
  TextField, MenuItem, Button, CircularProgress, Alert,
  Paper,
  Divider, InputAdornment, Tooltip, IconButton, Switch,
  FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Payments as PaymentsIcon,
  Search as SearchIcon, Refresh as RefreshIcon,
  Person as PersonIcon, School as ClassIcon,
  AccountBalance as BankIcon, AttachMoney as MoneyIcon,
  CheckCircle as PaidIcon, Warning as PartialIcon, ReportProblem as OverdueIcon,
  ReceiptLong as ReceiptIcon, RestartAlt as ResetIcon,
  ClearAll as ClearAllIcon, DoneAll as PayAllIcon, Bolt as FullIcon,
  Sms as SmsIcon, WhatsApp as WhatsAppIcon, PhoneAndroid as MomoIcon,
  AutoAwesome as AutoIcon,
  AccountBalanceWallet as WalletIcon, EventNote as DateIcon,
  Edit as AdjustIcon, Group as ClassPayIcon,
  Description as ReportIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';
import ReceiptDialog from './ReceiptDialog';
import OtherReceiptDialog from './OtherReceiptDialog';
import OtherPaymentTab from './OtherPaymentTab';
import AdjustBalancesTab from './AdjustBalancesTab';
import ReceiptsTab from './ReceiptsTab';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PAYMENT_MODES = [
  { v: 'Cash',          label: 'Cash' },
  { v: 'Bank Transfer', label: 'Bank Transfer' },
  { v: 'Mobile Money',  label: 'Mobile Money' },
  { v: 'Cheque',        label: 'Cheque' },
  { v: 'POS',           label: 'POS / Card' },
];

const TABS = [
  { v: 'fee',         label: 'Fee Payment',         icon: <PaymentsIcon fontSize="small" />,    enabled: true  },
  { v: 'other',       label: 'Other / Class',       icon: <ClassPayIcon fontSize="small" />,    enabled: true  },
  { v: 'adjust',      label: 'Adjust Balances',     icon: <AdjustIcon fontSize="small" />,      enabled: true  },
  { v: 'receipts',    label: 'Receipts',            icon: <ReportIcon fontSize="small" />,      enabled: true  },
];

// ── SMS receipt template ───────────────────────────────────────
// Short enough to fit in one SMS part (160 GSM-7 chars) once tokens
// are substituted for a typical Ghanaian name + 3-figure amounts.
// Tokens supported: {name} {paid} {balance} {total_paid} {receipt} {school}
const SMS_RECEIPT_KEY = 'payments.sms_receipt_template.v1';
const DEFAULT_SMS_RECEIPT_TEMPLATE =
  'Hi, we received GHS {paid} from {name} (Rcpt {receipt}). Paid so far: GHS {total_paid}. Outstanding: GHS {balance}. Thanks — {school}.';

function renderSmsTemplate(tpl, vars) {
  return String(tpl || '')
    .replaceAll('{name}',       vars.name       || '')
    .replaceAll('{paid}',       vars.paid       || '')
    .replaceAll('{balance}',    vars.balance    || '')
    .replaceAll('{total_paid}', vars.total_paid || '')
    .replaceAll('{receipt}',    vars.receipt    || '')
    .replaceAll('{school}',     vars.school     || '');
}

// ── Status helpers ──────────────────────────────────────────────
// Recognised states:
//   CREDIT   — paid more than the bill (school owes the student)
//   PAID     — bill fully settled, balance == 0
//   PARTIAL  — some payment made but balance still owing
//   OWING    — nothing paid yet
function balanceStatus(a) {
  const bal  = Number(a.balance || 0);
  const paid = Number(a.t_paid || 0);
  const due  = Number(a.total_due || 0);
  if (bal < -0.01)                       return { tone: 'info',    label: 'CREDIT',  icon: <PaidIcon sx={{ fontSize: 14 }} /> };
  if (Math.abs(bal) <= 0.01 && due > 0)  return { tone: 'success', label: 'PAID',    icon: <PaidIcon sx={{ fontSize: 14 }} /> };
  if (paid > 0 && bal > 0)               return { tone: 'warning', label: 'PARTIAL', icon: <PartialIcon sx={{ fontSize: 14 }} /> };
  if (bal > 0)                           return { tone: 'error',   label: 'OWING',   icon: <OverdueIcon sx={{ fontSize: 14 }} /> };
  return { tone: 'default', label: '—', icon: null };
}

// ── Per-account breakdown cell (used inside the stacked account cards) ─────
function BreakdownCell({ label, value, bold, tone }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, fontSize: 9.5, display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontWeight: bold ? 800 : 600,
        fontSize: 13,
        color: tone || 'text.primary',
        ...TNUM,
      }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Re-usable summary tile ─────────────────────────────────────
function SummaryTile({ icon, label, value, tone = 'primary' }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 1.5, py: 1, borderRadius: 2,
      bgcolor: (t) => alpha(t.palette[tone].main, 0.08),
      minWidth: 0,
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 1.5,
        bgcolor: (t) => alpha(t.palette[tone].main, 0.15),
        color: `${tone}.main`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 10, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={800} sx={TNUM} noWrap>{value}</Typography>
      </Box>
    </Box>
  );
}

// Which of the school's accounts is "the cash drawer".
//
// Matching used to be exact — account_type 'cash', or the name spelled
// precisely 'Cash-In-Hand' / 'Cash'. A school that typed its account as
// "CASH IN HAND" or "Cash in hand" matched nothing, so `bankAcc` stayed
// empty and Confirm Payment sat disabled with no explanation. Normalise
// away case, spaces and dashes so every ordinary spelling resolves.
function findCashAccount(list) {
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');
  return (list || []).find(a =>
    norm(a.account_type) === 'cash'
    || norm(a.name) === 'cashinhand'
    || norm(a.name) === 'cash'
  ) || null;
}

// ── Fee Payment Tab ────────────────────────────────────────────
function FeePaymentTab() {
  const { schoolName } = useAuth();

  // Reference data
  const [classes, setClasses] = useState([]);
  const [bankAccts, setBankAccts] = useState([]);

  // Picker
  const [students, setStudents] = useState([]);
  const [studentsLoading, setSL] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCla, setFilterCla] = useState('');
  const [includeLeavers, setIncLev] = useState(false);

  // Selection
  const [picked, setPicked] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [totals, setTotals] = useState({ total_due: 0, total_paid: 0, balance: 0 });
  const [accountsLoading, setAL] = useState(false);

  // Per-account "amount to pay" inputs (keyed by account name).
  // Any positive value is treated as a payment line — no separate checkbox.
  const [pay, setPay] = useState({});

  // Payment entry state
  const [payDate, setPayDate]     = useState(() => new Date().toISOString().slice(0, 10));
  const [mode, setMode]           = useState('Cash');
  const [bankAcc, setBankAcc]     = useState('');
  const [autoReceipt, setAutoR]   = useState(true);
  const [receiptNo, setReceipt]   = useState('');
  const [sendSMS, setSendSMS]     = useState(false);
  const [sendWA, setSendWA]       = useState(false);
  const [submitting, setSubmit]   = useState(false);

  // SMS receipt template — persists per-cashier so a customised wording
  // survives page reloads. Reset button restores the built-in default.
  const [smsTemplate, setSmsTemplate] = useState(() => {
    try {
      return localStorage.getItem(SMS_RECEIPT_KEY) || DEFAULT_SMS_RECEIPT_TEMPLATE;
    } catch { return DEFAULT_SMS_RECEIPT_TEMPLATE; }
  });
  const [smsTplOpen, setSmsTplOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(SMS_RECEIPT_KEY, smsTemplate); } catch {}
  }, [smsTemplate]);

  // Receipt dialog state — opens automatically after a payment, and can be
  // re-opened from the Recent Receipts panel.
  const [receiptOpen, setReceiptOpen]     = useState(false);
  const [receiptData, setReceiptData]     = useState(null);
  const [receiptLoading, setReceiptLd]    = useState(false);
  const [receiptError, setReceiptErr]     = useState('');

  // Other / Class receipts also appear in this student's list, and they
  // print from a different template.
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherData, setOtherData] = useState(null);
  const [otherLoading, setOtherLd] = useState(false);
  const [otherError, setOtherErr] = useState('');

  // Past receipts for the picked student
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLd] = useState(false);
  const [showOlderReceipts, setShowOlderReceipts] = useState(false);

  // Active accounting period + terms — drives "this term" grouping on the
  // Recent Receipts panel so the cashier focuses on the current term first.
  const [activePeriod, setActivePeriod] = useState(null);
  useEffect(() => {
    API.get('/accounting/periods/active')
      .then(r => setActivePeriod(r.data || null))
      .catch(() => setActivePeriod(null));
  }, []);
  const currentTerm = useMemo(() => {
    if (!activePeriod?.terms) return null;
    const today = new Date().toISOString().slice(0, 10);
    return activePeriod.terms.find(t =>
      today >= (t.starts_on || '').slice(0, 10) && today <= (t.ends_on || '').slice(0, 10)
    ) || null;
  }, [activePeriod]);

  // ── Loaders ───────────────────────────────────────────────────
  useEffect(() => {
    API.get('/classes').then(r => setClasses(r.data || [])).catch(() => {});
    API.get('/payments/bank-accounts').then(r => {
      const list = r.data || [];
      setBankAccts(list);
      const cash = findCashAccount(list);
      if (cash) setBankAcc(cash.name);
    }).catch(() => {});
  }, []);

  // Server-side search. Pre-search loads the first 500 alphabetical so
  // the cashier sees something immediately; once they start typing, we
  // refetch with `q` so the LIKE filter runs in MySQL and the 500 cap
  // no longer truncates students past the M's. Debounced 280ms.
  const loadStudents = async (qOverride) => {
    setSL(true);
    try {
      const q = (qOverride ?? search).trim();
      const r = await API.get('/payments/students', {
        params: {
          limit: 500,
          ...(q ? { q } : {}),
          ...(filterCla ? { cla: filterCla } : {}),
          ...(includeLeavers ? { includeLeavers: 1 } : {}),
        },
      });
      setStudents(r.data || []);
    } catch { /* silent */ }
    finally { setSL(false); setSearchPending(false); }
  };
  // `searchPending` flips on the moment the user types so the spinner
  // shows during the 280ms debounce gap too — without it the list
  // briefly looks stale ("No matches") until the request actually fires.
  const [searchPending, setSearchPending] = useState(false);
  useEffect(() => {
    setSearchPending(true);
    const t = setTimeout(() => { loadStudents(); }, 280);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [filterCla, includeLeavers, search]);
  const isSearching = studentsLoading || searchPending;

  // Split recent receipts: "this term" (current academic-year + current
  // term) vs everything older. The current bucket sits at the top, the
  // older bucket is hidden behind a "Show older" toggle so the cashier
  // works against this term's bills first.
  const recentSplit = useMemo(() => {
    const current = [];
    const older   = [];
    const accYer = activePeriod?.acc_year_label || activePeriod?.name || '';
    const termName = currentTerm?.name || '';
    for (const r of recent) {
      const matchesYear = !accYer || (r.acc_yer || '').trim() === accYer;
      const matchesTerm = !termName || (r.term || '').trim() === termName;
      if (matchesYear && matchesTerm) current.push(r);
      else                            older.push(r);
    }
    return { current, older };
  }, [recent, activePeriod, currentTerm]);

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;

    // Ranked search. Cashier types "21" — they want MSH_21 at the top,
    // not MSH_2110 followed by MSH_21 buried at line 47. The score
    // tiers go: exact > prefix > substring, applied to (full index_no,
    // its numeric tail, first name, last name, full name). Higher
    // score wins; equal scores fall back to alphabetical so the list
    // stays stable while the operator types.
    const scoreOf = (s) => {
      const id    = String(s.index_no || '').toLowerCase();
      const tail  = id.replace(/^[a-z_]+/, '');       // strip "msh_" etc.
      const first = String(s.firstname || '').toLowerCase();
      const last  = String(s.lastname  || '').toLowerCase();
      const full  = `${first} ${last}`.trim();

      // Exact (1000s)
      if (id    === term) return 1000;
      if (tail  === term) return 990;
      if (full  === term) return 960;
      if (first === term || last === term) return 950;

      // Prefix (800s)
      if (id.startsWith(term))    return 880;
      if (tail.startsWith(term))  return 870;
      if (first.startsWith(term)) return 820;
      if (last.startsWith(term))  return 810;
      if (full.startsWith(term))  return 800;

      // Substring (500s)
      if (id.includes(term))    return 580;
      if (tail.includes(term))  return 570;
      if (first.includes(term)) return 520;
      if (last.includes(term))  return 510;
      if (full.includes(term))  return 500;

      return 0;
    };

    const scored = [];
    for (const s of students) {
      const score = scoreOf(s);
      if (score > 0) scored.push({ s, score });
    }
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aId = String(a.s.index_no || '');
      const bId = String(b.s.index_no || '');
      return aId.localeCompare(bId, undefined, { numeric: true });
    });
    return scored.map(x => x.s);
  }, [students, search]);

  // ── Load fee accounts and past receipts when a student is picked ──
  useEffect(() => {
    if (!picked) {
      setAccounts([]); setTotals({ total_due: 0, total_paid: 0, balance: 0 });
      setPay({}); setRecent([]);
      return;
    }
    setAL(true);
    API.get(`/payments/students/${picked.index_no}/fee-accounts`)
      .then(r => {
        setAccounts(r.data?.accounts || []);
        setTotals(r.data?.totals || { total_due: 0, total_paid: 0, balance: 0 });
        setPay({});
      })
      .catch(() => { setAccounts([]); setTotals({ total_due: 0, total_paid: 0, balance: 0 }); })
      .finally(() => setAL(false));

    setRecentLd(true);
    API.get(`/payments/students/${picked.index_no}/payments`)
      .then(r => setRecent(r.data || []))
      .catch(() => setRecent([]))
      .finally(() => setRecentLd(false));
  }, [picked]);

  // Open the receipt dialog for a given payment id — used both by the
  // automatic post-confirm flow and by the Recent Receipts panel.
  const openReceipt = async (paymentId) => {
    setReceiptOpen(true);
    setReceiptLd(true);
    setReceiptData(null);
    setReceiptErr('');
    try {
      const r = await API.get(`/payments/${paymentId}/receipt`);
      setReceiptData(r.data);
    } catch (e) {
      setReceiptErr(swal.pickError(e, 'Could not load the receipt'));
    } finally { setReceiptLd(false); }
  };

  // Open whichever receipt a listed row actually is.
  //
  // This panel lists every payment carrying the student's index number,
  // and an Other / Class purchase records the student too — so a uniform
  // bought at the counter appears here alongside the fee receipts. It
  // used to be opened with the FEE receipt regardless, which printed a
  // uniform as a "Fee Payment Receipt" complete with a summary of the
  // fees account the purchase never touched. Route by scope instead.
  const openListedReceipt = async (rcpt) => {
    if (rcpt.payment_scope && rcpt.payment_scope !== 'FEE') {
      if (!rcpt.transaction_id) {
        swal.info(
          `Receipt ${rcpt.receipt_no || ''} · GHS ${fmtMoney(rcpt.total_amount)}\n`
          + 'This was recorded outside the fee flow and has no printable detail here. '
          + 'Open it from the Receipts tab.',
          'Other payment',
        );
        return;
      }
      setOtherOpen(true); setOtherLd(true); setOtherData(null); setOtherErr('');
      try {
        const r = await API.get(`/other-payments/${rcpt.transaction_id}/receipt`);
        setOtherData(r.data);
      } catch (e) {
        setOtherErr(swal.pickError(e, 'Could not load the receipt'));
      } finally { setOtherLd(false); }
      return;
    }
    openReceipt(rcpt.payment_id);
  };

  // ── Computed payment lines from the entry inputs ──────────────
  // Any account with an amount > 0 typed into its row becomes a payment line.
  const paymentLines = useMemo(() => {
    return accounts
      .filter(a => Number(pay[a.account]) > 0)
      .map(a => ({ account: a.account, amount: Number(pay[a.account]) }));
  }, [accounts, pay]);

  const amountReceived = useMemo(
    () => paymentLines.reduce((s, l) => s + l.amount, 0),
    [paymentLines]
  );

  // ── Validation helpers ────────────────────────────────────────
  const canSubmit =
    picked && paymentLines.length > 0 && payDate && mode && bankAcc &&
    (autoReceipt || receiptNo.trim()) && !submitting;

  // Why Confirm Payment is disabled, in the order a cashier fills the
  // form. A disabled button with no reason is a guessing game: the cash
  // account in particular is invisible, because it is chosen for you.
  const blockedBecause = useMemo(() => {
    if (!picked)                 return 'Select a student first.';
    if (paymentLines.length === 0) return 'Enter an amount on at least one fee account.';
    if (!payDate)                return 'Pick a payment date.';
    if (!mode)                   return 'Choose a payment mode.';
    if (!bankAcc)                return mode === 'Cash'
      ? 'This school has no active cash account. Add one under Finance → Accounts, or choose another mode.'
      : 'Choose the bank account the money went into.';
    if (!autoReceipt && !receiptNo.trim())
      return 'Enter a receipt number, or switch Auto on.';
    return '';
  }, [picked, paymentLines.length, payDate, mode, bankAcc, autoReceipt, receiptNo]);

  // ── Helpers for amount inputs ─────────────────────────────────
  const setPayFor = (acc, value) =>
    setPay(prev => ({ ...prev, [acc]: value }));

  const fillFullFor = (a) =>
    setPay(prev => ({ ...prev, [a.account]: Number(a.balance || 0).toFixed(2) }));

  const fillAllBalances = () => {
    const next = {};
    for (const a of accounts) {
      if (Number(a.balance) > 0) next[a.account] = Number(a.balance).toFixed(2);
    }
    setPay(next);
  };
  const clearAllAmounts = () => setPay({});

  const reset = () => {
    setPicked(null); setAccounts([]); setPay({});
    setReceipt(''); setAutoR(true);
  };

  // ── Submit ────────────────────────────────────────────────────
  const submit = async () => {
    if (!canSubmit) return;
    const linesHtml = paymentLines.map(l =>
      `<li>${l.account} · GHS ${fmtMoney(l.amount)}</li>`
    ).join('');
    const receiptLabel = autoReceipt ? 'auto-generated' : receiptNo.trim();
    const ok = await swal.confirm({
      title: 'Confirm Payment',
      tone:  'primary',
      icon:  'question',
      html:  `
        <div style="text-align:left;font-size:0.92rem">
          <div><b>${picked.firstname} ${picked.lastname}</b> · ${picked.index_no}</div>
          <div style="margin-top:6px;color:#475569">${mode} · ${bankAcc} · Receipt ${receiptLabel}</div>
          <ul style="margin:8px 0 0 18px;padding:0">${linesHtml}</ul>
          <div style="margin-top:10px;font-weight:800">
            Total: GHS ${fmtMoney(amountReceived)}
          </div>
        </div>`,
      confirmText: 'Yes, record payment',
    });
    if (!ok) return;

    setSubmit(true);
    try {
      const term    = accounts[0]?.term    || '';
      const acc_yer = accounts[0]?.acc_yer || '';
      const r = await API.post('/payments/record', {
        student_id:   picked.index_no,
        pay_date:     payDate,
        mode,
        bank_account: bankAcc,
        receipt_no:   autoReceipt ? null : receiptNo.trim(),
        term,
        acc_yer,
        lines: paymentLines,
      });
      // Pop the receipt dialog straight away. Balances + history refresh in
      // the background; closing the receipt drops the cashier back to a
      // clean form ready for the next student.
      setSubmit(false);
      const newId = r.data?.paymentId;
      if (newId) {
        openReceipt(newId);
      } else {
        swal.error('Payment recorded but no receipt id returned.');
      }

      const [accRes, recRes] = await Promise.all([
        API.get(`/payments/students/${picked.index_no}/fee-accounts`),
        API.get(`/payments/students/${picked.index_no}/payments`),
      ]);
      setAccounts(accRes.data?.accounts || []);
      setTotals(accRes.data?.totals || { total_due: 0, total_paid: 0, balance: 0 });
      setRecent(recRes.data || []);
      setPay({}); setReceipt('');

      // ── SMS receipt to guardian ────────────────────────────────
      // Fire-and-forget: any gateway failure is logged in sms_log and
      // never blocks the cashier flow. Uses the post-payment totals
      // so {balance} / {total_paid} reflect the receipt just issued.
      if (sendSMS) {
        try {
          const t = accRes.data?.totals || { total_due: 0, total_paid: 0, balance: 0 };
          const recRows = recRes.data || [];
          const receiptRow = recRows.find(x => x.paymentId === newId) || recRows[0];
          const receiptLabel = receiptRow?.receipt_no || receiptRow?.receiptNo || '—';
          const body = renderSmsTemplate(smsTemplate, {
            name:       (picked.firstname || '').trim(),
            paid:       fmtMoney(amountReceived),
            balance:    fmtMoney(t.balance),
            total_paid: fmtMoney(t.total_paid),
            receipt:    receiptLabel,
            school:     schoolName || 'School',
          }).slice(0, 160);
          const phoneRes = await API.get('/sms/recipients', {
            params: { audience: 'individual', student_ind: picked.index_no, channel: 'parent' },
          });
          const rec = (phoneRes.data?.recipients || [])[0];
          if (rec?.phone) {
            await API.post('/sms/send', {
              recipients: [{ phone: rec.phone, student_ind: picked.index_no, class_name: picked.calss || null }],
              message: body,
              kind: 'receipt',
            });
          } else {
            swal.warning?.('Receipt SMS skipped — no phone on file for guardian.');
          }
        } catch (smsErr) {
          console.warn('SMS receipt failed:', smsErr?.response?.data || smsErr.message);
        }
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not record payment'));
      setSubmit(false);
    }
  };

  const classOptions = useMemo(() => (classes || []).map(c => c.class).filter(Boolean), [classes]);

  return (
    <Box>
      {/* Sticky student strip */}
      {picked && (
        <Card sx={{ mb: 2, position: 'sticky', top: 8, zIndex: 4 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontWeight: 700 }}>
                  {(picked.firstname?.[0] || '?').toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} noWrap>
                    {picked.firstname} {picked.lastname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {picked.index_no} · {picked.calss || '—'} · {picked.type || '—'}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 2 }}>
                <SummaryTile icon={<MoneyIcon />}   label="Outstanding"  value={`GHS ${fmtMoney(totals.balance)}`}    tone="error" />
                <SummaryTile icon={<PaidIcon />}    label="Paid So Far"  value={`GHS ${fmtMoney(totals.total_paid)}`} tone="success" />
                <SummaryTile icon={<ReceiptIcon />} label="Total Due"    value={`GHS ${fmtMoney(totals.total_due)}`}  tone="primary" />
              </Stack>
              <Tooltip title="Clear & pick another">
                <IconButton onClick={reset}><ResetIcon /></IconButton>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '0.9fr 1.6fr 1fr' },
        gap: 2,
      }}>
        {/* ── Picker ───────────────────────────────────── */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <PersonIcon color="primary" />
              <Typography fontWeight={800}>Select Student</Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Refresh">
                <span>
                  <IconButton size="small" onClick={loadStudents} disabled={studentsLoading}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack spacing={1.2} sx={{ mb: 1.5 }}>
              <TextField fullWidth size="small" placeholder="Search by ID or name"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: isSearching ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} thickness={5} />
                    </InputAdornment>
                  ) : null,
                }} />
              <TextField select size="small" label="Class" value={filterCla}
                onChange={(e) => setFilterCla(e.target.value)}>
                <MenuItem value=""><em>All classes</em></MenuItem>
                {classOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <FormControlLabel
                control={<Switch size="small" checked={includeLeavers}
                  onChange={(e) => setIncLev(e.target.checked)} />}
                label={<Typography variant="caption">Include leavers / completed</Typography>}
              />
            </Stack>

            {/* Stacked card layout — class & status fit on the same row,
                no horizontal scrolling needed in narrow column. */}
            <Box sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
              {isSearching && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    Searching…
                  </Typography>
                </Box>
              )}
              {!isSearching && filteredStudents.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">No matches.</Typography>
                </Box>
              )}
              {!isSearching && filteredStudents.map((s) => {
                const isPicked = picked?.index_no === s.index_no;
                const isLev  = s.status === 'leaver';
                const isDone = s.status === 'completed';
                const badgeLabel = isDone ? 'Completed' : isLev ? 'Leaver' : 'Active';
                const badgeColor = isDone ? 'info' : isLev ? 'default' : 'success';
                return (
                  <Paper
                    key={`${s.status}-${s.trans_code}`}
                    variant="outlined"
                    onClick={() => setPicked(s)}
                    sx={{
                      px: 1.2, py: 1, mb: 0.7, cursor: 'pointer',
                      borderColor: isPicked ? 'primary.main' : 'divider',
                      borderWidth: isPicked ? 2 : 1,
                      bgcolor: isPicked ? (t) => alpha(t.palette.primary.main, 0.08) : 'background.paper',
                      transition: 'all 0.12s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar sx={{
                        width: 34, height: 34, fontSize: 13, fontWeight: 700,
                        bgcolor: isDone ? 'info.main' : isLev ? 'grey.500' : 'primary.main',
                      }}>
                        {(s.firstname?.[0] || '?').toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={isPicked ? 800 : 700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {s.firstname} {s.lastname}
                          </Typography>
                          <Chip
                            size="small"
                            label={badgeLabel}
                            color={badgeColor}
                            variant="outlined"
                            sx={{ height: 18, fontSize: 9.5, fontWeight: 700, '& .MuiChip-label': { px: 0.7 } }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.2 }}>
                          <Typography variant="caption" color="text.secondary" sx={TNUM} noWrap>
                            {s.index_no}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">·</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {s.calss || 'No class'}
                            {isDone && s.completion_year ? ` · ${s.completion_year}` : ''}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          </CardContent>
        </Card>

        {/* ── Middle column: Recent Receipts + Fee Accounts ────────── */}
        <Box>
          {/* Recent Receipts — only shown once a student is picked.
              Receipts are grouped: current academic-year + term first,
              older terms folded behind a toggle so the printable receipt
              and the cashier's focus stay scoped to "this term". */}
          {picked && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ pb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" rowGap={0.5}>
                  <ReceiptIcon color="primary" />
                  <Typography fontWeight={800}>This Term's Receipts</Typography>
                  {currentTerm && (
                    <Chip size="small" color="primary" variant="outlined"
                      label={`${currentTerm.name}${activePeriod?.acc_year_label ? ` · ${activePeriod.acc_year_label}` : ''}`}
                      sx={{ fontWeight: 700 }} />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Chip size="small" label={`${recentSplit.current.length}`}
                    color={recentSplit.current.length > 0 ? 'primary' : 'default'}
                    sx={{ fontWeight: 700 }} />
                </Stack>
                {recentLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading…</Typography>
                  </Stack>
                ) : recent.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No previous receipts for this student yet.
                  </Typography>
                ) : recentSplit.current.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No receipts yet for this term.
                    {recentSplit.older.length > 0 && ` ${recentSplit.older.length} older receipt${recentSplit.older.length === 1 ? '' : 's'} available below.`}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {recentSplit.current.map(rcpt => {
                      const isOther = rcpt.payment_scope && rcpt.payment_scope !== 'FEE';
                      return (
                        <Tooltip key={rcpt.payment_id}
                          title={`${isOther ? `${rcpt.payment_type || 'Other payment'} · ` : ''}${rcpt.mode} · ${rcpt.received_by_name || ''}${rcpt.term ? ` · ${rcpt.term}` : ''}`.trim()}>
                          <Button
                            size="small" variant="outlined"
                            color={isOther ? 'secondary' : 'primary'}
                            startIcon={<ReceiptIcon fontSize="small" />}
                            onClick={() => openListedReceipt(rcpt)}
                            sx={{ textTransform: 'none', fontWeight: 700, ...TNUM }}
                          >
                            {rcpt.receipt_no}
                            <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 600 }}>
                              GHS {fmtMoney(rcpt.total_amount)}
                            </Box>
                            {/* Not a fee payment — say so, so nobody reads a
                                uniform purchase as money off the fees. */}
                            {isOther && (
                              <Box component="span" sx={{ ml: 0.8, fontSize: 10, fontWeight: 800, color: 'secondary.main' }}>
                                OTHER
                              </Box>
                            )}
                          </Button>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}

                {/* Older receipts — collapsed by default. Each one printed will
                    still scope to ITS OWN term thanks to the backend filter. */}
                {recentSplit.older.length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1.2, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10 }}>
                        Older terms
                      </Typography>
                      <Chip size="small" variant="outlined"
                        label={`${recentSplit.older.length}`}
                        sx={{ fontWeight: 700, height: 18 }} />
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" onClick={() => setShowOlderReceipts(s => !s)}
                        sx={{ fontWeight: 700, textTransform: 'none', fontSize: 11 }}>
                        {showOlderReceipts ? 'Hide' : 'Show'}
                      </Button>
                    </Stack>
                    {showOlderReceipts && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {recentSplit.older.slice(0, 24).map(rcpt => (
                          <Tooltip key={rcpt.payment_id}
                            title={`${rcpt.term || '—'} · ${rcpt.acc_yer || '—'} · ${rcpt.received_by_name || ''}`.trim()}>
                            <Button
                              size="small" variant="text"
                              startIcon={<ReceiptIcon fontSize="small" />}
                              onClick={() => openListedReceipt(rcpt)}
                              sx={{ textTransform: 'none', fontWeight: 600, ...TNUM, color: 'text.secondary' }}
                            >
                              {rcpt.receipt_no}
                              <Box component="span" sx={{ ml: 1, fontWeight: 600 }}>
                                GHS {fmtMoney(rcpt.total_amount)}
                              </Box>
                            </Button>
                          </Tooltip>
                        ))}
                        {recentSplit.older.length > 24 && (
                          <Chip size="small" label={`+${recentSplit.older.length - 24} more`}
                            sx={{ fontWeight: 700 }} />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Fee Accounts ─────────────────────────────── */}
          <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
              <WalletIcon color="primary" />
              <Typography fontWeight={800}>Fee Accounts</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" label={`${accounts.length} account(s)`} sx={{ fontWeight: 700 }} />
              {accounts.length > 0 && (
                <>
                  <Tooltip title="Fill every account with its full balance">
                    <span>
                      <Button size="small" variant="outlined" startIcon={<PayAllIcon />}
                        onClick={fillAllBalances} disabled={totals.balance <= 0}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Pay All
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Clear all amounts">
                    <span>
                      <IconButton size="small" onClick={clearAllAmounts}
                        disabled={Object.keys(pay).length === 0}>
                        <ClearAllIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>

            {!picked && (
              <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                <PersonIcon sx={{ fontSize: 40, mb: 1, color: 'text.disabled' }} />
                <Typography variant="body2">Pick a student to load their fee accounts.</Typography>
              </Box>
            )}

            {picked && accountsLoading && (
              <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
            )}

            {picked && !accountsLoading && accounts.length === 0 && (
              <Alert severity="info" variant="outlined">
                No fee accounts on record for this student. They may need an account assigned via
                <strong> Assign Student Account</strong> first.
              </Alert>
            )}

            {picked && !accountsLoading && accounts.length > 0 && (
              <Stack spacing={1.2}>
                {accounts.map((a) => {
                  const status = balanceStatus(a);
                  const typed = Number(pay[a.account]) > 0;
                  const typedAmt = Number(pay[a.account] || 0);
                  // Overpayment = whatever the cashier types beyond what's
                  // currently owing. We allow it (a parent may want to pay
                  // ahead for next term), but we surface a clear message
                  // explaining what the surplus means for the school.
                  const balanceNow = Number(a.balance || 0);
                  const overpayBy  = typedAmt > balanceNow + 0.01
                    ? Math.max(0, typedAmt - Math.max(0, balanceNow))
                    : 0;
                  // Existing standing credit on the account (the student
                  // already has a positive credit from a prior overpay).
                  const standingCredit = balanceNow < -0.01 ? Math.abs(balanceNow) : 0;
                  const balCol = balanceNow > 0 ? 'error.main'
                    : balanceNow < 0 ? 'info.main'
                    : 'text.disabled';
                  return (
                    <Paper
                      key={a.account}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderColor: typed ? 'primary.main' : 'divider',
                        borderWidth: typed ? 2 : 1,
                        bgcolor: typed ? (t) => alpha(t.palette.primary.main, 0.04) : 'background.paper',
                        transition: 'border-color 0.12s, background-color 0.12s',
                      }}
                    >
                      {/* Header — account + status + balance */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center"
                        sx={{ mb: 1.2, flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontFamily: FONT_ACCT, fontWeight: 800, fontSize: 14.5 }}>
                            {a.account}
                          </Typography>
                          {status.icon && (
                            <Chip size="small" label={status.label}
                              color={status.tone}
                              icon={status.icon}
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10, fontWeight: 800 }} />
                          )}
                        </Stack>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 9.5, display: 'block', lineHeight: 1 }}>
                            Balance
                          </Typography>
                          <Typography sx={{ fontWeight: 900, fontSize: 18, color: balCol, ...TNUM, lineHeight: 1.15 }}>
                            GHS {fmtMoney(a.balance)}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Detail grid — wraps to fill the available width */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                        gap: 1,
                        mb: 1.5,
                        p: 1.1,
                        borderRadius: 1.5,
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                      }}>
                        <BreakdownCell label="Arrears"     value={fmtMoney(a.arr)} />
                        <BreakdownCell label="Term Bill"   value={fmtMoney(a.tobe)} />
                        <BreakdownCell label="Scholarship" value={fmtMoney(a.scholarship_amount)} />
                        <BreakdownCell label="Adjustments" value={fmtMoney(Number(a.add_bal) - Number(a.wri))} />
                        <BreakdownCell label="Total Due"   value={fmtMoney(a.total_due)} bold />
                        <BreakdownCell label="Paid"        value={fmtMoney(a.t_paid)} tone="success.dark" />
                      </Box>

                      {/* Amount-to-pay row */}
                      <Stack direction="row" alignItems="center" spacing={1.2}>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 10 }}>
                          Amount to Pay
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Pay the outstanding balance in full">
                          <span>
                            <Button size="small" startIcon={<FullIcon sx={{ fontSize: 16 }} />}
                              onClick={() => fillFullFor(a)}
                              disabled={balanceNow <= 0}
                              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>
                              Full
                            </Button>
                          </span>
                        </Tooltip>
                        <TextField
                          size="small" type="number"
                          value={pay[a.account] ?? ''}
                          onChange={(e) => setPayFor(a.account, e.target.value)}
                          placeholder="0.00"
                          sx={{ width: 140, '& input': { textAlign: 'right', ...TNUM, fontWeight: 700 } }}
                        />
                      </Stack>

                      {/* Standing credit banner — for accounts that already
                          carry a credit balance from a previous overpay. */}
                      {standingCredit > 0 && typedAmt === 0 && (
                        <Box sx={{
                          mt: 1, p: 1.1, borderRadius: 1.5,
                          bgcolor: (t) => alpha(t.palette.info.main, 0.08),
                          borderLeft: '3px solid',
                          borderColor: 'info.main',
                        }}>
                          <Typography variant="caption" sx={{
                            display: 'block', fontWeight: 700, fontSize: 11.5,
                            color: 'info.dark',
                          }}>
                            This account is in credit. The school is currently holding
                            <strong> GHS {fmtMoney(standingCredit)} </strong>
                            on the student's behalf — it can be carried over to next term
                            or refunded.
                          </Typography>
                        </Box>
                      )}

                      {/* Overpayment narration — clear, plain English so the
                          cashier understands the surplus is a credit owed
                          back to the student (a liability for the school). */}
                      {overpayBy > 0 && (
                        <Box sx={{
                          mt: 1, p: 1.2, borderRadius: 1.5,
                          bgcolor: (t) => alpha(t.palette.warning.main, 0.10),
                          border: '1px solid',
                          borderColor: 'warning.light',
                        }}>
                          <Typography variant="caption" sx={{
                            display: 'block', fontWeight: 800, fontSize: 11,
                            textTransform: 'uppercase', letterSpacing: 0.5,
                            color: 'warning.dark', mb: 0.4,
                          }}>
                            Overpayment Notice
                          </Typography>
                          <Typography variant="body2" sx={{
                            fontSize: 12, color: 'text.primary', lineHeight: 1.45,
                          }}>
                            This payment is <strong>GHS {fmtMoney(overpayBy)}</strong> more
                            than what is currently owed on this account. The surplus will
                            be recorded as a <strong>credit balance</strong> in favour of
                            the student — a liability the school owes back, which can be
                            applied to future bills or refunded on request.
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </CardContent>
          </Card>
        </Box>

        {/* ── Payment Entry ────────────────────────────── */}
        <Card sx={{ alignSelf: 'flex-start', position: { lg: 'sticky' }, top: { lg: 100 } }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <ReceiptIcon color="primary" />
              <Typography fontWeight={800}>Payment Entry</Typography>
            </Stack>

            <Stack spacing={1.5}>
              <TextField fullWidth size="small" type="date" label="Payment Date"
                InputLabelProps={{ shrink: true }} value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" /></InputAdornment>,
                }} />

              <TextField select size="small" label="Mode" value={mode}
                onChange={(e) => {
                  const next = e.target.value;
                  setMode(next);
                  // Cash mode auto-binds to the Cash-In-Hand account so
                  // the cashier doesn't have to pick a bank manually.
                  if (next === 'Cash') {
                    const cash = findCashAccount(bankAccts);
                    setBankAcc(cash ? cash.name : '');
                  } else if (findCashAccount([{ name: bankAcc }])) {
                    // Switching off Cash mode — clear the cash binding so
                    // the user picks a bank explicitly.
                    setBankAcc('');
                  }
                }}
                fullWidth>
                {PAYMENT_MODES.map(m => <MenuItem key={m.v} value={m.v}>{m.label}</MenuItem>)}
              </TextField>

              {mode === 'Cash' ? (
                // Cash mode: locked to the school's cash account. We still
                // render the field so the layout doesn't shift.
                //
                // It shows `bankAcc` and NOTHING ELSE. It used to fall back
                // to the literal 'Cash-In-Hand' for display, so a school
                // with no active cash account saw a filled-in field while
                // the state behind it was empty — and Confirm Payment
                // stayed disabled with the reason invisible.
                <TextField size="small" label="Cash Account" fullWidth
                  value={bankAcc}
                  error={!bankAcc}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" /></InputAdornment>,
                  }}
                  helperText={bankAcc
                    ? `Cash mode posts directly to ${bankAcc}`
                    : 'No active cash account found for this school — add one under Finance → Accounts, or pick another payment mode.'} />
              ) : (
                <TextField select size="small" label="Bank Account" value={bankAcc}
                  onChange={(e) => setBankAcc(e.target.value)} fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" /></InputAdornment>,
                  }}>
                  <MenuItem value=""><em>— Select bank —</em></MenuItem>
                  {bankAccts
                    .filter(b => String(b.account_type || '').toLowerCase() !== 'cash'
                              && b.name !== 'Cash-In-Hand')
                    .map(b => (
                      <MenuItem key={b.trans_code} value={b.name}>
                        {b.name}
                        <Box component="span" sx={{ ml: 0.5, color: 'text.disabled', fontSize: 11 }}>
                          · {b.account_type || ''}
                        </Box>
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {/* Receipt Number — auto by default, manual on toggle */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 10 }}>
                    Receipt Number
                  </Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch size="small" checked={autoReceipt}
                        onChange={(e) => setAutoR(e.target.checked)} />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Auto
                      </Typography>
                    }
                  />
                </Stack>
                <TextField
                  size="small" fullWidth
                  value={autoReceipt ? '' : receiptNo}
                  onChange={(e) => setReceipt(e.target.value)}
                  disabled={autoReceipt}
                  placeholder={autoReceipt ? 'Auto-generated on save' : 'Type a custom receipt number'}
                  required={!autoReceipt}
                  InputProps={{
                    startAdornment: autoReceipt ? (
                      <InputAdornment position="start">
                        <AutoIcon fontSize="small" color="primary" />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>

              <Divider />

              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.08) }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: 10 }}>
                      Amount Received
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {paymentLines.length} line{paymentLines.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={900} color="success.dark" sx={TNUM}>
                    GHS {fmtMoney(amountReceived)}
                  </Typography>
                </Stack>
              </Box>

              <Button
                size="large" variant="contained" color="primary"
                disabled={!canSubmit} onClick={submit}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ReceiptIcon />}
                sx={{ fontWeight: 800 }}
              >
                {submitting ? 'Recording…' : 'Confirm Payment'}
              </Button>

              {!canSubmit && !submitting && blockedBecause && (
                <Typography variant="caption" color="warning.dark"
                  sx={{ display: 'block', mt: 0.6, fontWeight: 600, textAlign: 'center' }}>
                  {blockedBecause}
                </Typography>
              )}

              {/* ── After Payment — delivery & MoMo trigger ────────── */}
              <Box sx={{
                p: 1.5, borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                border: (t) => `1px dashed ${alpha(t.palette.primary.main, 0.3)}`,
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10 }}>
                    Send Receipt / Trigger Payment
                  </Typography>
                </Stack>

                <Stack spacing={0.6}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch size="small" checked={sendSMS}
                        onChange={(e) => setSendSMS(e.target.checked)} />
                    }
                    label={
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        <SmsIcon fontSize="small" color={sendSMS ? 'primary' : 'action'} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          SMS receipt to guardian
                        </Typography>
                        {sendSMS && (
                          <Button size="small" onClick={() => setSmsTplOpen(v => !v)}
                            sx={{ ml: 0.5, minWidth: 0, px: 0.6, fontSize: 10, fontWeight: 700,
                              textTransform: 'none' }}>
                            {smsTplOpen ? 'Hide template' : 'Edit template'}
                          </Button>
                        )}
                      </Stack>
                    }
                  />
                  {sendSMS && smsTplOpen && (() => {
                    const preview = renderSmsTemplate(smsTemplate, {
                      name:       (picked?.firstname || 'Kwame'),
                      paid:       fmtMoney(amountReceived || 100),
                      balance:    fmtMoney(Math.max((totals.balance || 0) - (amountReceived || 0), 0)),
                      total_paid: fmtMoney((totals.total_paid || 0) + (amountReceived || 0)),
                      receipt:    'AUTO',
                      school:     schoolName || 'School',
                    });
                    const over = preview.length > 160;
                    return (
                      <Box sx={{ pl: 4.5, pr: 0.5 }}>
                        <TextField multiline minRows={2} maxRows={4} size="small" fullWidth
                          value={smsTemplate}
                          onChange={(e) => setSmsTemplate(e.target.value)}
                          helperText={
                            <span>
                              Tokens: <code style={{ fontSize: 10 }}>{'{name}'}</code>{' '}
                              <code style={{ fontSize: 10 }}>{'{paid}'}</code>{' '}
                              <code style={{ fontSize: 10 }}>{'{balance}'}</code>{' '}
                              <code style={{ fontSize: 10 }}>{'{total_paid}'}</code>{' '}
                              <code style={{ fontSize: 10 }}>{'{receipt}'}</code>{' '}
                              <code style={{ fontSize: 10 }}>{'{school}'}</code>
                            </span>
                          }
                          sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
                        <Box sx={{
                          mt: 0.6, p: 0.8, borderRadius: 1,
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                          border: (t) => `1px dashed ${alpha(t.palette.primary.main, 0.25)}`,
                        }}>
                          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic',
                            color: 'text.secondary', mb: 0.3 }}>
                            Preview
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: 11,
                            color: over ? 'error.main' : 'text.primary', whiteSpace: 'pre-wrap' }}>
                            {preview}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Typography variant="caption"
                            sx={{ fontWeight: 700, color: over ? 'error.main' : 'text.secondary' }}>
                            {preview.length}/160 chars
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Button size="small" sx={{ minWidth: 0, fontSize: 10, textTransform: 'none' }}
                            onClick={() => setSmsTemplate(DEFAULT_SMS_RECEIPT_TEMPLATE)}>
                            Reset to default
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })()}
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch size="small" disabled checked={sendWA}
                        onChange={(e) => setSendWA(e.target.checked)} />
                    }
                    label={
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        <WhatsAppIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          WhatsApp receipt to guardian
                        </Typography>
                        <Chip size="small" label="Coming soon" variant="outlined"
                          sx={{ height: 16, fontSize: 9, fontWeight: 700, ml: 0.5 }} />
                      </Stack>
                    }
                  />
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Tooltip title="Mobile Money payment trigger — gateway integration coming soon">
                  <span style={{ width: '100%', display: 'block' }}>
                    <Button fullWidth size="small" variant="outlined" disabled
                      startIcon={<MomoIcon fontSize="small" />}
                      sx={{ fontWeight: 700, textTransform: 'none' }}>
                      Trigger Mobile Money payment
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Printable receipt — opens automatically after a successful payment,
          and on demand from the Recent Receipts panel. */}
      <ReceiptDialog
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        data={receiptData}
        loading={receiptLoading}
        error={receiptError}
      />

      {/* An Other / Class receipt listed against this student prints from
          its own template — never the fee one. */}
      <OtherReceiptDialog
        open={otherOpen}
        onClose={() => setOtherOpen(false)}
        data={otherData}
        loading={otherLoading}
        error={otherError}
      />
    </Box>
  );
}

// ── Placeholder for tabs not in Phase 1 ────────────────────────
// ── Page ───────────────────────────────────────────────────────
export default function PaymentsPage() {
  const [tab, setTab] = useState('fee');

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', fontFamily: FONT_ACCT }}>
      <ModuleHeader
        icon={<PaymentsIcon />}
        title="Day Payment"
        subtitle="Cashier desk · daily intake of fees and other school income"
      />

      <Card sx={{ mb: 2.5, position: 'sticky', top: 0, zIndex: 5 }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': { minHeight: 56, fontWeight: 700, textTransform: 'none', fontSize: 13.5 },
          }}
        >
          {TABS.map(t => (
            <Tab key={t.v} value={t.v} icon={t.icon} iconPosition="start"
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{t.label}</span>
                  {!t.enabled && (
                    <Chip size="small" label={t.soon ? 'later' : 'soon'}
                      sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.7 } }} />
                  )}
                </Stack>
              } />
          ))}
        </Tabs>
      </Card>

      {tab === 'fee'         && <FeePaymentTab />}
      {tab === 'other'       && <OtherPaymentTab />}
      {tab === 'adjust'      && <AdjustBalancesTab />}
      {tab === 'receipts'    && <ReceiptsTab />}
    </Box>
  );
}
